import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { actionNames } from "@newsela/snitch";
import { UserProperties } from "@/types/models/UserProperties.interface";
import useAttachmentsContext from "../utils/useAttachmentsContext";
import { IGuidedHighlightingLevel } from "../utils/useGuidedHighlightingStudent/types";
import useGuidedHighlightingStudent from "../utils/useGuidedHighlightingStudent/useGuidedHighlightingStudent";
import {
  panelWrapperCss,
  scrollContainerCss,
} from "./GuidedHighlightingContainer.style";
import GuidedHighlightingStudentInfoPopup from "./GuidedHighlightingStudentInfoPopup";
import GuidedHighlightingStudentSkillQuestion from "./GuidedHighlightingStudentSkillQuestion";
import GuidedHighlightingTeacherSkillQuestion from "./GuidedHighlightingTeacherSkillQuestion";
import {
  handleGuidedHighlightingActiveQuestion,
  toggleGuidedHighlightingActivityPanel,
} from "./utils/utils";
import GuidedHighlightingCompleteMessage from "./GuidedHighlightingCompleteMessage/GuidedHighlightingCompleteMessage";
import GuidedHighlightingPanelHeader from "./GuidedHighlightingPanelHeader/GuidedHighlightingPanelHeader";

interface IProps {
  closeButtonEventProperties: object;
  shouldFocusCloseButtonRef: object;
  legacyArticleId: number;
  onClickCloseButton: () => void;
  userId: number | string;
  userProperties: UserProperties;
  contentId: string;
  level: IGuidedHighlightingLevel;
  guidedHighlightId: string;
}

const GuidedHighlightingContainer = ({
  closeButtonEventProperties,
  onClickCloseButton,
  shouldFocusCloseButtonRef,
  level,
  userProperties,
}: IProps) => {
  const router = useRouter();
  const {
    handleActiveQuestion,
    isCompleted,
    guidedHighlightingData,
    getGuidedHighlightingData,
    handleCompleteActivity,
    shouldShowIRCTokens,
  } = useGuidedHighlightingStudent();
  const [tokensEarned, setTokensEarned] = useState(0);
  const [isGuidedHighlightingPopupActive, setIsGuidedHighlightingPopupActive] =
    useState(false);
  const { attachments } = useAttachmentsContext();
  const {
    query: { levelId },
  } = router;

  const { isStudent } = userProperties;
  useEffect(() => {
    void getGuidedHighlightingData(attachments).then(
      (guidedHighlightingData) => {
        handleGuidedHighlightingActiveQuestion(
          guidedHighlightingData,
          handleActiveQuestion,
        );
        handleCompleteActivity(guidedHighlightingData);
      },
    );
  }, [attachments]);

  useEffect(() => {
    toggleGuidedHighlightingActivityPanel(router, true);
    handleGuidedHighlightingActiveQuestion(
      guidedHighlightingData,
      handleActiveQuestion,
    );
  }, [level]);

  useEffect(() => {
    if (!localStorage.getItem("skip_guidedHighlighting_popup")) {
      setIsGuidedHighlightingPopupActive(true);
    }
  }, []);

  useEffect(() => {
    if (guidedHighlightingData && guidedHighlightingData?.questions?.length) {
      const newTokenCount =
        guidedHighlightingData.questions.reduce(
          (accumulator, { tokensEarned = 0 }) => accumulator + tokensEarned,
          0,
        ) || 0;
      setTokensEarned(newTokenCount);
    }
  }, [guidedHighlightingData]);

  const onClickBackButton = () => {
    toggleGuidedHighlightingActivityPanel(router, false, true);
    handleGuidedHighlightingActiveQuestion(undefined, handleActiveQuestion);
  };

  const backButtonEventProperties = {
    objectType: "GuidedHighlighting",
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "Article",
      description: "Navigate back to Activity Picker from Guided Highlighting",
      content_id: levelId,
    },
  };

  const activateGuidedHighlightingPopup = () => {
    localStorage.removeItem("skip_guigedhighlighting_popup");
    setIsGuidedHighlightingPopupActive(true);
  };

  return (
    <div css={panelWrapperCss}>
      {isStudent ? (
        <GuidedHighlightingStudentInfoPopup
          active={isGuidedHighlightingPopupActive}
          setActive={setIsGuidedHighlightingPopupActive}
        />
      ) : (
        <></>
      )}
      <GuidedHighlightingPanelHeader
        backButtonEventProperties={backButtonEventProperties}
        activateGuidedHighlightingPopup={activateGuidedHighlightingPopup}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        closeButtonEventProperties={closeButtonEventProperties}
        isStudent={isStudent}
        onClickBackButton={onClickBackButton}
        onClickCloseButton={onClickCloseButton}
      />
      <div css={scrollContainerCss}>
        {guidedHighlightingData?.questions?.map((question, index) => {
          return isStudent ? (
            <GuidedHighlightingStudentSkillQuestion
              question={question}
              key={index}
            />
          ) : (
            <GuidedHighlightingTeacherSkillQuestion
              question={question}
              index={index}
            />
          );
        })}

        <GuidedHighlightingCompleteMessage
          isCompleted={isCompleted}
          tokensEarned={tokensEarned}
          isStudent={isStudent}
          shouldShowTokens={shouldShowIRCTokens}
        />
      </div>
    </div>
  );
};

export default GuidedHighlightingContainer;
